import { gql } from '@apollo/client';
import { ORDER_FIELDS } from '@/graphql/queries/orders';
import { ITEM_FIELDS } from '@/graphql/queries/items';
import { ROUTE_FIELDS } from '@/graphql/queries/routes';
import { RATING_FIELDS } from '@/graphql/queries/ratings';
import { CLIENT_FIELDS } from '@/graphql/queries/clients';
import { USER_FIELDS } from '@/graphql/queries/users';
import { STOP_FIELDS } from '@/graphql/queries/stops';
import { LOCATION_FIELDS } from '@/graphql/queries/locations';
import { PRICING_OVERRIDE_FIELDS } from '@/graphql/fragments/pricing_overrides';
import { LISTING_FIELDS } from '@/graphql/queries/listings';
import { BID_FIELDS } from '@/graphql/queries/bids';
import { TEAMMATE_FIELDS } from '@/graphql/queries/teammates';
import { ORDER_ROUTE_MAPPINGS } from '@/graphql/queries/order_route_mappings';
import { MANIFEST_FIELDS } from '@/graphql/queries/manifests';
import { NOTE_FIELDS } from '@/graphql/queries/notes';
import { SURVEY_QUESTION_FIELDS, SURVEY_RESPONSE_FIELDS } from '@/graphql/fragments/surveys';
import { ORDER_ROUTE_FIELDS } from '@/graphql/queries/orders';
import { EXCEPTION_MAPPINGS_FIELD } from '@/graphql/fragments/exception_mappings';
import { SERVICES_FIELDS } from '@/graphql/fragments/services';

export const ORDER_NOTES = gql`
    ${NOTE_FIELDS}
    query GetOrderNotes($order_id: uuid!, $notes_privacy: notes_bool_exp!) {
        notes(where: { _and: [{ order_id: { _eq: $order_id } }, $notes_privacy] }, order_by: { created_at: desc }) {
            ...NoteFields
        }
    }
`;

export const ORDER_BY_ID = gql`
    ${ORDER_FIELDS}
    ${ITEM_FIELDS}
    ${ROUTE_FIELDS}
    ${RATING_FIELDS}
    ${CLIENT_FIELDS}
    ${USER_FIELDS}
    ${STOP_FIELDS}
    ${LOCATION_FIELDS}
    ${LISTING_FIELDS}
    ${BID_FIELDS}
    ${TEAMMATE_FIELDS}
    ${ORDER_ROUTE_MAPPINGS}
    ${MANIFEST_FIELDS}
    ${SURVEY_RESPONSE_FIELDS}
    ${SURVEY_QUESTION_FIELDS}
    ${ORDER_ROUTE_FIELDS}
    ${NOTE_FIELDS}
    ${PRICING_OVERRIDE_FIELDS}
    ${SERVICES_FIELDS}

    query GetOrderByID($order_id: uuid!) {
        orders(where: { order_id: { _eq: $order_id } }) {
            ...OrderFields
            shipper_billable
            split_seq
            original_order {
                order_id
                order_number
            }
            middle_mile_carrier_invoice_id
            warehouse_carrier_invoice_id
            services {
                ...ServicesFields
            }
            survey_responses {
                ...SurveyResponseFields
                survey_question {
                    ...SurveyQuestionFields
                }
            }
            itemsByOrderId {
                ...ItemFields
                inventory_unit {
                    received_at
                    closed_at
                }
                manifests {
                    mapping_id
                    manifest_id
                    manifest {
                        ...ManifestFields
                    }
                }
            }
            routes {
                ...OrderRouteMappings
                route {
                    ...RouteFields
                    stopsByRouteId {
                        ...StopFields
                    }
                    teammateByDriverId {
                        ...TeammateFields
                    }
                }
            }
            order_shipper {
                ...ClientFields
                user {
                    ...UserFields
                }
                locations {
                    ...LocationFields
                }
            }
            order_carrier {
                ...ClientFields
                user {
                    ...UserFields
                }
                locations {
                    ...LocationFields
                }
            }
            order_middle_mile_carrier {
                ...ClientFields
                user {
                    ...UserFields
                }
                locations {
                    ...LocationFields
                }
            }
            listing {
                ...ListingFields
                bids {
                    ...BidFields
                }
            }
            ratings {
                ...RatingFields
            }
            messages {
                message_id
                link_type
            }
            shipper_rate_override {
                ...PricingOverrideFields

                assign {
                    client_id
                    business_name
                    profile_img
                    profile_img_color
                }

                partner_client {
                    client_id
                    business_name
                    profile_img
                    profile_img_color
                }
            }
            carrier_rate_override {
                ...PricingOverrideFields

                assign {
                    client_id
                    business_name
                    profile_img
                    profile_img_color
                }

                partner_client {
                    client_id
                    business_name
                    profile_img
                    profile_img_color
                }
            }
        }
    }
`;

/** Every order linked to the given one through `original_order_id`, at any depth, for the breadcrumb and title menu. */
export const LINKED_ORDERS = gql`
    query GetLinkedOrders($order_id: uuid!) {
        order: orders_by_pk(order_id: $order_id) {
            order_id
            linked_family {
                order_id
                order_number
                po_number
                original_order_id
                split_seq
                order_status
                created_at
            }
        }
    }
`;

/** A split parent's children with the fields `calcMilestone` reads and each item's exception mappings, in split order. */
export const SPLIT_CHILDREN = gql`
    ${ORDER_FIELDS}
    ${ITEM_FIELDS}
    query SplitChildren($parent_id: uuid!) {
        orders(
            where: { original_order_id: { _eq: $parent_id }, split_seq: { _is_null: false } }
            order_by: { split_seq: asc }
        ) {
            ...OrderFields
            split_seq
            itemsByOrderId {
                ...ItemFields
            }
            carrier_invoice {
                carrier_invoice_id
                status
            }
            listing {
                listing_id
                bids {
                    bid_id
                    bid_status
                }
            }
        }
    }
`;
